import "@fontsource/playfair-display/400.css";
import "@fontsource/playfair-display/500.css";
import "@fontsource/playfair-display/600.css";
import "@fontsource/playfair-display/700.css";
import "@fontsource/outfit/300.css";
import "@fontsource/outfit/400.css";
import "@fontsource/outfit/500.css";
import "@fontsource/outfit/600.css";
import "@fontsource/outfit/700.css";
import "@fontsource/tajawal/400.css";
import "@fontsource/tajawal/500.css";
import "@fontsource/tajawal/700.css";
import "@fontsource/noto-serif-sc/400.css";
import "@fontsource/noto-serif-sc/500.css";
import "@fontsource/noto-serif-sc/700.css";

import "@/App.css";
import { LangProvider } from "@/contexts/LangContext";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Destinations from "@/components/Destinations";
import Culture from "@/components/Culture";
import Cuisine from "@/components/Cuisine";
import WhyUs from "@/components/WhyUs";
import Festivals from "@/components/Festivals";
import TravelTips from "@/components/TravelTips";
import Visa from "@/components/Visa";
import TravelGuide from "@/components/TravelGuide";
import FeaturedExperiences from "@/components/FeaturedExperiences";
import Services from "@/components/Services";
import Education from "@/components/Education";
import CantonFair from "@/components/CantonFair";
import ItineraryBuilder from "@/components/ItineraryBuilder";
import BookYourTrip from "@/components/BookYourTrip";
import GlobalFlightsHighlight from "@/components/GlobalFlightsHighlight";
import FAQ from "@/components/FAQ";
import ContactForm from "@/components/ContactForm";
import Footer from "@/components/Footer";
import AIChat from "@/components/AIChat";
import PaymentResult from "@/components/PaymentResult";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import ChinaBusinessExplorer from "@/pages/ChinaBusinessExplorer";
import BlogIndex from "@/pages/BlogIndex";
import BlogCategory from "@/pages/BlogCategory";
import BlogPost from "@/pages/BlogPost";
import FlightSearch from "@/pages/FlightSearch";
import B2BLogin from "@/pages/B2BLogin";
import Activate from "@/pages/Activate";
import ResetPassword from "@/pages/ResetPassword";
import Portal from "@/pages/Portal";
import FlightOrders from "@/pages/FlightOrders";
import AdminPlatform from "@/pages/AdminPlatform";
import AdminOrgDetail from "@/pages/AdminOrgDetail";
import { AuthProvider } from "@/auth/AuthContext";
import { CartProvider } from "@/journeys/CartContext";
import CuratedJourneys from "@/components/CuratedJourneys";
import JourneysShop from "@/pages/JourneysShop";
import JourneyDetail from "@/pages/JourneyDetail";
import { CartPage, JourneyCheckout, BookingResult } from "@/pages/JourneyCommerce";
import AdminCommerce from "@/pages/AdminCommerce";
import MyJourneys from "@/pages/MyJourneys";
import SEO from "@/components/SEO";
import { trackPageView } from "@/lib/analytics";

const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
};

const HashScroller = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const timer = setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.hash]);
  return null;
};

const HOME_JSONLD = {
  "@context": "https://schema.org",
  "@type": "TravelAgency",
  name: "The China Explorer",
  description:
    "Dubai-based travel company specialising in bespoke tours to China — destinations, culture, cuisine, business travel, factory visits and Mandarin programs.",
  url: "https://thechinaexplorer.com",
  logo: "https://thechinaexplorer.com/img/og/og-default.jpg",
  telephone: "+971589656886",
  email: "hello@thechinaexplorer.com",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Dubai",
    addressCountry: "AE",
  },
  areaServed: { "@type": "Country", name: "China" },
  sameAs: [
    "https://www.instagram.com/the_china_explorer/",
    "https://www.linkedin.com/company/the-china-explorer/",
  ],
};

const MainLanding = () => (
  <div className="App min-h-screen bg-[#F7F5F0] text-[#121212] font-body antialiased">
    <SEO
      title="The China Explorer — Bespoke Tours to China from Dubai"
      description="Discover China with The China Explorer — luxury cultural tours, business travel, factory visits, Mandarin programs and educational study tours, designed from Dubai."
      path="/"
      keywords={[
        "china travel",
        "china tours from dubai",
        "luxury china tours",
        "business travel china",
        "canton fair packages",
        "factory visits china",
        "mandarin learning china",
        "china travel agency",
        "tours to china",
        "thechinaexplorer",
      ]}
      jsonLd={HOME_JSONLD}
    />
    <Header />
    <main>
      <Hero />
      <WhyUs />
      <Destinations />
      <Culture />
      <Cuisine />
      <Festivals />
      <Visa />
      <TravelTips />
      <FeaturedExperiences />
      <Services />
      <CuratedJourneys />
      <GlobalFlightsHighlight />
      <Education />
      <CantonFair />
      <ItineraryBuilder />
      <BookYourTrip />
      <TravelGuide />
      <FAQ />
      <ContactForm />
    </main>
    <Footer />
    <AIChat />
    <PaymentResult />
  </div>
);

function App() {
  return (
    <LangProvider>
      <BrowserRouter>
        <AuthProvider>
        <CartProvider>
        <RouteTracker />
        <HashScroller />
        <Routes>
          <Route path="/" element={<MainLanding />} />
          <Route path="/china-business-explorer" element={<ChinaBusinessExplorer />} />
          <Route path="/flights" element={<FlightSearch />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:category" element={<BlogCategory />} />
          <Route path="/blog/:category/:slug" element={<BlogPost />} />
          <Route path="/login" element={<B2BLogin />} />
          <Route path="/activate" element={<Activate />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/portal" element={<Portal />} />
          <Route path="/portal/flight-orders" element={<FlightOrders />} />
          <Route path="/admin" element={<AdminPlatform />} />
          <Route path="/admin/organizations/:orgId" element={<AdminOrgDetail />} />
          <Route path="/journeys" element={<JourneysShop />} />
          <Route path="/journeys/cart" element={<CartPage />} />
          <Route path="/journeys/checkout" element={<JourneyCheckout />} />
          <Route path="/journeys/booking" element={<BookingResult />} />
          <Route path="/journeys/:slug" element={<JourneyDetail />} />
          <Route path="/admin/commerce" element={<AdminCommerce />} />
          <Route path="/my-journeys" element={<MyJourneys />} />
        </Routes>
        </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </LangProvider>
  );
}

export default App;
